import { FEEDING_RATES } from './feedingRates';

const rateFields = Object.entries(FEEDING_RATES).flatMap(([key, rate]) => [rate.graph, 'alarms_by_' + key, key + '_values']);

const compactCache = new WeakMap();

const cloneObject = (value) => (value && typeof value === "object" ? { ...value } : value);

const cloneTimedSeries = (items = []) =>
  Array.isArray(items) ? items.map((item) => cloneObject(item)) : [];

const cloneEntryWithArrayValue = (entry) => ({
  day: entry.day,
  value: cloneTimedSeries(entry?.value),
});

const cloneValuesSummaryEntry = (entry) => ({
  day: entry.day,
  value: { ...(entry?.value || {}) },
});

// Las bandas se acumulan sumando sobre el primer valor visto, así que hay que copiarlas en
// profundidad: mutar el bucket original alteraría los datos de la cámara que aportó la base.
const cloneDepthStrataEntry = (entry) => ({
  day: entry.day,
  value: entry?.value && typeof entry.value === "object"
    ? {
      ...entry.value,
      bins: Object.fromEntries(
        Object.entries(entry.value.bins || {}).map(([band, bucket]) => [band, { ...bucket }])
      ),
    }
    : null,
});

const cloneAlarmsByTasaEntry = (entry) => ({
  day: entry.day,
  value: {
    baja: {
      value: entry?.value?.baja?.value ?? 0,
      tasas: [...(entry?.value?.baja?.tasas || [])],
    },
    media: {
      value: entry?.value?.media?.value ?? 0,
      tasas: [...(entry?.value?.media?.tasas || [])],
    },
    alta: {
      value: entry?.value?.alta?.value ?? 0,
      tasas: [...(entry?.value?.alta?.tasas || [])],
    },
  },
});

const createBucket = () => ({
  items: [],
  byDay: new Map(),
});

const ensureBucketEntry = (bucket, entry, cloneEntry) => {
  let existing = bucket.byDay.get(entry.day);
  let isNew = false;

  if (!existing) {
    existing = cloneEntry(entry);
    bucket.byDay.set(entry.day, existing);
    bucket.items.push(existing);
    isNew = true;
  }

  return { existing, isNew };
};

const mergeRateEntry = (bucket, entry, derived) => {
  const { existing, isNew } = ensureBucketEntry(bucket, entry, cloneEntryWithArrayValue);
  if (isNew && !derived) return;
  if (isNew) {
    existing.value = existing.value.map((item) => ({
      time: item.time,
      value: item.value,
      derived: true,
      ...(item.synthetic ? { synthetic: true, source_time: item.source_time } : {}),
    }));
    return;
  }

  const timeMap = new Map(existing.value.map((item) => [item.time, {
    time: item.time,
    value: item.value,
    derived: true,
    ...(item.synthetic ? { synthetic: true, source_time: item.source_time } : {}),
  }]));
  (entry.value || []).forEach((item) => {
    const current = timeMap.get(item.time);
    timeMap.set(item.time, {
      time: item.time,
      // Las cámaras de una misma jaula informan la tasa del mismo alimentador: sumarlas duplicaba
      // una cifra que el cliente conoce. El máximo deduplica valores idénticos sin inventar
      // alimentación que no ocurrió.
      value: current ? Math.max(current.value, item.value) : item.value,
      derived: true,
      ...((!current && item.synthetic)
        || (current?.synthetic && item.synthetic && current.source_time === item.source_time)
        ? { synthetic: true, source_time: item.source_time }
        : {}),
    });
  });

  existing.value = Array.from(timeMap.values()).sort(
    (left, right) => new Date(left.time) - new Date(right.time)
  );
};

const createCompactedItem = (name) => ({
  ...Object.fromEntries(rateFields.map((field) => [field, createBucket()])),
  name,
  note_camera_ids: [],
  alarms_by_tasa_levels: [],
  alarms_data: createBucket(),
  depth_analytics: createBucket(),
  depth_strata: createBucket(),
  feeding_time_range: [],
  graph_perdidos: createBucket(),
  values_summary: createBucket(),
  analytics_schema_version: createBucket(),
});

const finalizeCompactedItem = (item) => ({
  ...item,
  ...Object.fromEntries(rateFields.map((field) => [field, item[field].items])),
  alarms_data: item.alarms_data.items,
  depth_analytics: item.depth_analytics.items,
  depth_strata: item.depth_strata.items,
  graph_perdidos: item.graph_perdidos.items,
  values_summary: item.values_summary.items,
  analytics_schema_version: item.analytics_schema_version.items,
});

export function CompactarDatos(data) {
  if (!data || typeof data !== "object") return { analytic_data: {} };

  const cachedResult = compactCache.get(data);
  if (cachedResult) {
    return cachedResult;
  }

  const result = {};
  const source = data?.analytic_data || data;
  if (!source || typeof source !== "object") return { analytic_data: {} };
  const cameraCountByBaseName = Object.values(source).reduce((counts, jaula) => {
    const baseName = (jaula?.name || "").replace(/[^0-9]/g, "");
    counts[baseName] = (counts[baseName] || 0) + 1;
    return counts;
  }, {});

  let jaulaIndex = 0;

  for (const key in source) {
    const jaula = source[key];
    if (!jaula || !jaula.name) continue;

    const baseName = (jaula.name || "").replace(/[^0-9]/g, "");
    const jaulaName = jaula.name;
    const isMultiCameraGroup = cameraCountByBaseName[baseName] > 1;

    if (!result[baseName]) {
      result[baseName] = createCompactedItem(baseName);
    }

    const compacted = result[baseName];
    if (!compacted.note_camera_ids.includes(key)) compacted.note_camera_ids.push(key);

    const graphKey = jaulaIndex === 0 ? "graph_activity" : `graph_activity${jaulaIndex}`;
    compacted[graphKey] = {
      name: jaulaName,
      data: cloneTimedSeries(jaula.graph_activity),
    };

    Object.values(FEEDING_RATES).forEach(({ graph }) => {
      (jaula[graph] || []).forEach((entry) => mergeRateEntry(compacted[graph], entry, isMultiCameraGroup));
    });

    (jaula.graph_perdidos || []).forEach((entry) => {
      const { existing, isNew } = ensureBucketEntry(
        compacted.graph_perdidos,
        entry,
        cloneEntryWithArrayValue
      );
      if (isNew && !isMultiCameraGroup) return;
      if (isNew) {
        existing.value = existing.value.map((item) => ({
          time: item.time,
          value: item.value,
          derived: true,
        }));
        return;
      }

      const timeMap = new Map();
      [...(existing.value || []), ...(entry.value || [])].forEach((item) => {
        timeMap.set(item.time, (timeMap.get(item.time) || 0) + item.value);
      });

      const merged = Array.from(timeMap, ([time, value]) => ({ time, value, derived: true })).sort(
        (left, right) => new Date(left.time) - new Date(right.time)
      );

      for (let index = 1; index < merged.length; index += 1) {
        if (merged[index].value < merged[index - 1].value) {
          merged[index].value = merged[index - 1].value;
        }
      }

      existing.value = merged;
    });

    Object.keys(FEEDING_RATES).forEach((key) => {
      const field = key + '_values';
      (jaula[field] || []).forEach((entry) => {
        const { existing } = ensureBucketEntry(compacted[field], entry, cloneEntryWithArrayValue);
        existing.value = [...new Set([...(existing.value || []), ...(entry.value || [])])];
      });
    });

    // El grupo compactado vale por su miembro más antiguo. Es informativo, para auditar o dirigir
    // recómputos: los lectores resuelven v1/v2 por estructura (`snapshot ?? inline`), no por aquí.
    (jaula.analytics_schema_version || []).forEach((entry) => {
      const { existing, isNew } = ensureBucketEntry(
        compacted.analytics_schema_version,
        entry,
        (source) => ({ day: source.day, value: source.value })
      );
      if (isNew) return;
      existing.value = Math.min(existing.value, entry.value);
    });

    // El winche es compartido por las cámaras de una jaula: conservar la ruta con mayor cobertura
    // evita contar dos veces el mismo minuto, igual que en la analítica local.
    (jaula.depth_analytics || []).forEach((entry) => {
      const normalizedEntry = {
        ...entry,
        value: Array.isArray(entry?.value) ? entry.value : [entry?.value],
      };
      const { existing, isNew } = ensureBucketEntry(
        compacted.depth_analytics,
        normalizedEntry,
        cloneEntryWithArrayValue
      );
      if (isNew) return;

      const routes = new Map();
      [...(existing.value || []), ...(normalizedEntry.value || [])].filter(Boolean).forEach((route) => {
        const identity = `${route.winche_id}|${route.id_sensor}|${route.bin_width_m}|${route.min_valid_m}|${route.feeding_scope || 'center'}`;
        const current = routes.get(identity);
        const measured = (route.fed?.total || 0) + (route.unfed?.total || 0);
        const currentMeasured = (current?.fed?.total || 0) + (current?.unfed?.total || 0);
        if (!current || measured > currentMeasured) routes.set(identity, route);
      });
      existing.value = Array.from(routes.values());
    });

    // Los perdidos son de cada cámara y se suman; los kg son de la jaula. `Comida_entregada` viene
    // replicada en cada cámara: sumarla multiplicaría el alimento, y promediarla inventaría un
    // valor que nadie midió. Por eso lo no sumable (kg y los contadores de cobertura, que tampoco
    // admiten suma: dos cámaras darían 48 h de un día) conserva el primer valor visto y describe a
    // esa cámara, mientras `perdidos` sí es el total del grupo.
    (jaula.depth_strata || []).forEach((entry) => {
      const { existing, isNew } = ensureBucketEntry(
        compacted.depth_strata,
        entry,
        cloneDepthStrataEntry
      );
      if (isNew) return;
      if (!existing.value || !entry?.value) return;
      // Bandas de distinto ancho son profundidades distintas con el mismo número: no se mezclan.
      if (existing.value.bin_width_m !== entry.value.bin_width_m
        || existing.value.min_valid_m !== entry.value.min_valid_m) {
        existing.value = null;
        return;
      }
      Object.entries(entry.value.bins || {}).forEach(([band, bucket]) => {
        const current = existing.value.bins[band];
        if (!current) {
          existing.value.bins[band] = { ...bucket };
          return;
        }
        current.perdidos += bucket.perdidos;
        current.intervalos += bucket.intervalos;
      });
    });

    (jaula.alarms_data || []).forEach((entry) => {
      const sourcedEntry = {
        ...entry,
        value: (entry.value || []).map((alarm) => ({ ...alarm, camera_id: key })),
      };
      const { existing, isNew } = ensureBucketEntry(
        compacted.alarms_data,
        sourcedEntry,
        cloneEntryWithArrayValue
      );
      if (isNew) return;

      existing.value = [...(existing.value || []), ...cloneTimedSeries(sourcedEntry.value)].sort(
        (left, right) => new Date(left.start) - new Date(right.start)
      );
    });

    Object.keys(FEEDING_RATES).forEach((key) => {
      const field = 'alarms_by_' + key;
      (jaula[field] || []).forEach((entry) => {
        const { existing, isNew } = ensureBucketEntry(
          compacted[field],
          entry,
          cloneAlarmsByTasaEntry
        );
        if (isNew) return;

        existing.value.baja.value += entry?.value?.baja?.value ?? 0;
        existing.value.media.value += entry?.value?.media?.value ?? 0;
        existing.value.alta.value += entry?.value?.alta?.value ?? 0;

        existing.value.baja.tasas.push(...(entry?.value?.baja?.tasas || []));
        existing.value.media.tasas.push(...(entry?.value?.media?.tasas || []));
        existing.value.alta.tasas.push(...(entry?.value?.alta?.tasas || []));
      });
    });

    // (jaula.values_summary || []).forEach((entry) => {
    //   const { existing, isNew } = ensureBucketEntry(
    //     compacted.values_summary,
    //     entry,
    //     cloneValuesSummaryEntry
    //   );
    //   if (isNew) return;

    //   const fieldsSum = [
    //     "alarms_by_ton",
    //     "alarms_by_visits",
    //     "alarms_count",
    //     "perdidos_count",
    //   ];

    //   fieldsSum.forEach((field) => {
    //     existing.value[field] = (existing.value[field] ?? 0) + (entry?.value?.[field] ?? 0);
    //   });
      
    //   // conservar solo el ayuno de la primera jaula
    //   if (existing.value.fasts_count === undefined) {
    //     existing.value.fasts_count = entry?.value?.fasts_count;
    //   }

    //   if (existing.value.delivered_food === undefined) {
    //     existing.value.delivered_food = entry?.value?.delivered_food;
    //   }

    //   if (existing.value.visitas_count === undefined) {
    //     existing.value.visitas_count = entry?.value?.visitas_count;
    //   }

    //   if (existing.value.sfr_value === undefined) {
    //     existing.value.sfr_value = entry?.value?.sfr_value;
    //   }
    // });
    (jaula.values_summary || []).forEach((entry) => {
      const { existing, isNew } = ensureBucketEntry(
        compacted.values_summary,
        entry,
        cloneValuesSummaryEntry
      );
      if (isNew) return;

      const fieldsSum = [
        "alarms_by_ton",
        "alarms_by_visits",
        "alarms_count",
        "perdidos_count",
      ];

      fieldsSum.forEach((field) => {
        existing.value[field] = (existing.value[field] ?? 0) + (entry?.value?.[field] ?? 0);
      });

      // NO sumar ayunos: conservar el de la primera jaula
      if (existing.value.fasts_count === undefined) {
        existing.value.fasts_count = entry?.value?.fasts_count;
      }

      if (existing.value.delivered_food === undefined) {
        existing.value.delivered_food = entry?.value?.delivered_food;
      }

      if (existing.value.visitas_count === undefined) {
        existing.value.visitas_count = entry?.value?.visitas_count;
      }

      if (existing.value.sfr_value === undefined) {
        existing.value.sfr_value = entry?.value?.sfr_value;
      }
    });

    jaulaIndex += 1;
  }

  const compactedResult = {
    ...data,
    analytic_data: Object.fromEntries(
      Object.entries(result).map(([name, item]) => [name, finalizeCompactedItem(item)])
    ),
  };

  compactCache.set(data, compactedResult);
  return compactedResult;
}
